import { useEffect, useRef } from "react";
import { Image } from "~/types/Image.ts";
import { NamedEntity } from "~/types/NamedEntity.ts";
import type { RowLayout } from "../home/itemRowProps.ts";
import { homeColors, homeSpacing } from "../home/theme.ts";
import { DragOffset } from "../home/useDraggableRow.tsx";
import { DragSnapshot, useDragState } from "../home/useDragState.ts";
import { EntityActions, EntityCard, EntityMenuAction } from "./EntityCard.tsx";
import { EntityCardPreview } from "./EntityCardPreview.tsx";
import { EntityCopy } from "./entityStyles.ts";
import { FadeScrollView, FadeScrollViewRef } from "./FadeScrollView.tsx";
import { orderEntityLayouts } from "./orderEntityLayouts.ts";
import { useFlashHighlight } from "./useFlashHighlight.ts";

const SCROLL_DELAY_MS = 100;
const HIGHLIGHT_DELAY_MS = 300;

type EntityScrollProps = {
  entities: NamedEntity[];
  actions: EntityActions;
  copy: EntityCopy;
  colors: Record<string, string>;
  drag: ReturnType<typeof useDragState>;
  onDrop: (snapshot: DragSnapshot, layouts: Record<string, RowLayout>) => void;
  computeDropIndex: (ids: string[], snapshot: DragSnapshot, layouts: Record<string, RowLayout>) => number | null;
  dragEnabled?: boolean;
  readOnlyIds?: Set<string>;
  itemCounts: Record<string, number>;
  images: Image[];
  onImagePress: (entityId: string, image?: Image) => void;
  imageLoading?: string | null;
  hideImagePlaceholder?: boolean;
  showImageMenuAction?: boolean;
  getMenuItems?: (entity: NamedEntity) => EntityMenuAction[];
};

export const EntityScroll = (props: EntityScrollProps) => {
  const {
    entities,
    actions,
    copy,
    colors,
    drag,
    onDrop,
    computeDropIndex,
    dragEnabled = true,
    readOnlyIds,
    itemCounts,
    images,
    onImagePress,
    imageLoading,
    hideImagePlaceholder,
    showImageMenuAction,
    getMenuItems,
  } = props;
  const ids = entities.map((e) => e.id);
  const layouts = orderEntityLayouts(ids, drag.layouts);
  const dropIndex = dragEnabled ? computeDropIndex(ids, drag.snapshot, layouts) : null;
  const originalIndex = drag.snapshot ? ids.indexOf(drag.snapshot.id) : -1;
  const wouldMove = dropIndex !== null && dropIndex !== originalIndex;
  const showBelow = wouldMove && (drag.snapshot?.offsetY ?? 0) > 0;
  const scrollRef = useRef<FadeScrollViewRef>(null);
  const prevIds = useRef(new Set(entities.map((e) => e.id)));
  const { highlightId, highlightOpacity, flash } = useFlashHighlight();

  useEffect(() => {
    const currentIds = new Set(entities.map((e) => e.id));
    const newEntity = entities.find((e) => !prevIds.current.has(e.id));
    prevIds.current = currentIds;
    if (newEntity) {
      setTimeout(() => scrollRef.current?.scrollToEnd({ animated: true }), SCROLL_DELAY_MS);
      setTimeout(() => flash(newEntity.id), HIGHLIGHT_DELAY_MS);
    }
  }, [entities, flash]);

  return (
    <FadeScrollView ref={scrollRef} style={{ flex: 1 }} drag={drag}>
      <div style={{ position: "relative", display: "flex", flexDirection: "column", gap: homeSpacing.xs }}>
        {entities.map((entity) => {
          const image = images.find((img) => img.typeId === entity.id);
          const isReadOnly = readOnlyIds?.has(entity.id) ?? false;
          return (
            <EntityCard
              key={entity.id}
              entity={entity}
              entities={entities}
              actions={actions}
              copy={copy}
              color={colors[entity.id]}
              hidden={drag.snapshot?.id === entity.id}
              highlightOpacity={highlightId === entity.id ? highlightOpacity : undefined}
              dragEnabled={dragEnabled}
              readOnly={isReadOnly}
              itemCount={itemCounts[entity.id] ?? 0}
              image={image}
              imageLoading={imageLoading === entity.id}
              hideImagePlaceholder={hideImagePlaceholder}
              showImageMenuAction={showImageMenuAction && !isReadOnly}
              menuItems={getMenuItems?.(entity) ?? []}
              onImagePress={() => onImagePress(entity.id, image)}
              onLayout={(layout: RowLayout) => drag.recordLayout(entity.id, layout)}
              onDragStart={dragEnabled ? () => drag.start(entity.id, "") : undefined}
              onDragMove={dragEnabled ? (offset: DragOffset) => drag.move(entity.id, offset) : undefined}
              onDragEnd={dragEnabled ? () => drag.end((snapshot) => snapshot && onDrop(snapshot, layouts)) : undefined}
            />
          );
        })}
        {dragEnabled && <DropIndicator dropIndex={dropIndex} entities={entities} layouts={layouts} below={showBelow} />}
        {dragEnabled && <GhostRow entities={entities} drag={drag.snapshot} layouts={layouts} />}
      </div>
    </FadeScrollView>
  );
};

type GhostProps = {
  entities: NamedEntity[];
  drag: DragSnapshot;
  layouts: Record<string, RowLayout>;
};

const GhostRow = ({ entities, drag, layouts }: GhostProps) => {
  if (!drag) return null;
  const layout = layouts[drag.id];
  if (!layout) return null;
  const entity = entities.find((e) => e.id === drag.id);
  if (!entity) return null;
  return (
    <div
      style={{
        position: "absolute",
        zIndex: 10,
        pointerEvents: "none",
        opacity: 0.85,
        top: layout.y + drag.offsetY,
        height: layout.height,
        width: layout.width,
      }}
    >
      <EntityCardPreview entity={entity} />
    </div>
  );
};

type DropIndicatorProps = {
  dropIndex: number | null;
  entities: NamedEntity[];
  layouts: Record<string, RowLayout>;
  below: boolean;
};

const DropIndicator = ({ dropIndex, entities, layouts, below }: DropIndicatorProps) => {
  if (dropIndex === null) return null;
  const targetId = entities[dropIndex]?.id;
  if (!targetId) return null;
  const layout = layouts[targetId];
  if (!layout) return null;
  const top = below ? layout.y + layout.height - 2 : layout.y - 2;
  return (
    <div
      style={{
        position: "absolute",
        left: -12,
        right: -12,
        height: 2,
        borderRadius: 1,
        zIndex: 15,
        pointerEvents: "none",
        top,
        backgroundColor: homeColors.dropIndicator,
      }}
    />
  );
};
